// ─── Types ────────────────────────────────────────────────────────────────────

export type CellObjectType =
    | "circle"
    | "square"
    | "rounded-square"
    | "triangle"
    | "diamond"
    | "star-4"
    | "star-6"
    | "cross";

export const CELL_OBJECT_TYPES: CellObjectType[] = [
    "circle",
    "square",
    "rounded-square",
    "triangle",
    "diamond",
    "star-4",
    "star-6",
    "cross",
];

export const CELL_OBJECT_LABELS: Record<CellObjectType, string> = {
    circle: "Circle",
    square: "Square",
    "rounded-square": "Rounded square",
    triangle: "Triangle",
    diamond: "Diamond",
    "star-4": "4-pointed star",
    "star-6": "6-pointed star",
    cross: "Cross",
};

export type ShapeMode = "uniform" | "per-ring" | "per-sector" | "random";
export type ScalarMode = "uniform" | "radial-in" | "radial-out" | "random";

export type RingPopulationRule =
    | { type: "all" }
    | { type: "randomDensity"; density: number; seedOffset?: number }
    | { type: "everyN"; step: number; phase: number }
    | { type: "fillNSkipN"; fill: number; skip: number; phase: number };

export interface RingConfig {
    sectors: number;
    rotationDeg: number;
    heightWeight: number;
    populationRule: RingPopulationRule;
    /** 0–3: multiplier relative to ring radial span */
    cellHeightFactor: number;
    /** -1 means ring uses shape mode fallback. */
    objectSvgIndex?: number;
}

export interface PolarObjectSvg {
    name: string;
    content: string;
}

export interface PolarGridCellsConfig {
    rings: number;
    /** Legacy fallback used only to build default ring configs. */
    sectors: number;
    ringConfigs: RingConfig[];
    /** 0 = solid disk, approaches 1 = thin outermost ring */
    innerRadiusFraction: number;
    /** 0–1: fraction of each cell's available arc removed as padding */
    cellPadding: number;

    shapeMode: ShapeMode;
    /** -1 uses built-in fallback shape fields below. */
    uniformObjectSvgIndex: number;
    /** Used when shapeMode = "per-ring"; cycles if shorter than rings */
    perRingObjectSvgIndices: number[];
    /** Used when shapeMode = "per-sector"; cycles if shorter than sectors */
    perSectorObjectSvgIndices: number[];
    uniformShape: CellObjectType;
    /** Used when shapeMode = "per-ring"; cycles if shorter than rings */
    perRingShapes: CellObjectType[];
    /** Used when shapeMode = "per-sector"; cycles if shorter than sectors */
    perSectorShapes: CellObjectType[];

    sizeMode: ScalarMode;
    /** 0–1: fraction of the cell's available arc size when sizeMode is "uniform" */
    uniformSize: number;
    sizeMin: number;
    sizeMax: number;

    opacityMode: ScalarMode;
    uniformOpacity: number;
    opacityMin: number;
    opacityMax: number;

    fill: string;
    /** Rotate each shape to align with the outward radial direction */
    rotateWithCell: boolean;

    useCustomSvg: boolean;
    /** Raw SVG string pasted by the user; used when useCustomSvg is true */
    svgSymbol: string;
    objectSvgs: PolarObjectSvg[];

    seed: number;
    canvasSize: number;
}

export const DEFAULT_RING_POPULATION_RULE: RingPopulationRule = { type: "all" };

export const DEFAULT_POLAR_GRID_CELLS_CONFIG: PolarGridCellsConfig = {
    rings: 5,
    sectors: 12,
    ringConfigs: Array.from({ length: 5 }, () => makeDefaultRingConfig(12)),
    innerRadiusFraction: 0.15,
    cellPadding: 0.2,
    shapeMode: "uniform",
    uniformObjectSvgIndex: -1,
    perRingObjectSvgIndices: [],
    perSectorObjectSvgIndices: [],
    uniformShape: "circle",
    perRingShapes: ["circle", "square", "triangle", "diamond", "star-4"],
    perSectorShapes: ["circle", "square", "triangle", "diamond"],
    sizeMode: "uniform",
    uniformSize: 0.8,
    sizeMin: 0.3,
    sizeMax: 0.95,
    opacityMode: "uniform",
    uniformOpacity: 1,
    opacityMin: 0.2,
    opacityMax: 1,
    fill: "#1a1a1a",
    rotateWithCell: false,
    useCustomSvg: false,
    svgSymbol: "",
    objectSvgs: [],
    seed: 42,
    canvasSize: 800,
};

export function makeDefaultRingConfig(sectors = 12): RingConfig {
    return {
        sectors: Math.max(1, Math.round(sectors)),
        rotationDeg: 0,
        heightWeight: 1,
        populationRule: DEFAULT_RING_POPULATION_RULE,
        cellHeightFactor: 0.9,
        objectSvgIndex: -1,
    };
}

export function normalizeRingConfigs(
    rings: number,
    ringConfigs: RingConfig[] | undefined,
    legacySectors: number,
): RingConfig[] {
    const ringCount = Math.max(1, Math.round(rings));
    const out: RingConfig[] = [];
    for (let i = 0; i < ringCount; i++) {
        const src = ringConfigs?.[i];
        const fallback = makeDefaultRingConfig(legacySectors);
        out.push({
            sectors: Math.max(1, Math.round(src?.sectors ?? fallback.sectors)),
            rotationDeg: Number.isFinite(src?.rotationDeg)
                ? (src?.rotationDeg as number)
                : fallback.rotationDeg,
            heightWeight: Math.max(
                0.01,
                Number.isFinite(src?.heightWeight)
                    ? (src?.heightWeight as number)
                    : fallback.heightWeight,
            ),
            populationRule: src?.populationRule ?? fallback.populationRule,
            cellHeightFactor: Math.max(
                0,
                Math.min(
                    3,
                    Number.isFinite(src?.cellHeightFactor)
                        ? (src?.cellHeightFactor as number)
                        : fallback.cellHeightFactor,
                ),
            ),
            objectSvgIndex:
                src?.objectSvgIndex == null
                    ? -1
                    : Math.max(-1, Math.floor(src.objectSvgIndex)),
        });
    }
    return out;
}

// ─── Seeded RNG (mulberry32) ──────────────────────────────────────────────────

function makeRng(seed: number): () => number {
    let s = (seed ^ 0xdeadbeef) >>> 0;
    return () => {
        s = (s + 0x6d2b79f5) >>> 0;
        let t = Math.imul(s ^ (s >>> 15), 1 | s);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

/** Per-cell independent RNG so results don't depend on iteration order. */
function cellRng(seed: number, ring: number, sector: number): () => number {
    const mixed = ((seed ^ ((ring * 65537 + sector) * 2654435761)) >>> 0);
    return makeRng(mixed);
}

// ─── Value resolvers ──────────────────────────────────────────────────────────

function resolveScalar(
    mode: ScalarMode,
    uniform: number,
    min: number,
    max: number,
    /** 0 = innermost ring, 1 = outermost ring */
    tRing: number,
    rng: () => number,
): number {
    switch (mode) {
        case "uniform":
            return uniform;
        case "radial-in":
            return max - tRing * (max - min);
        case "radial-out":
            return min + tRing * (max - min);
        case "random":
            return min + rng() * (max - min);
    }
}

function resolveShape(
    ring: number,
    sector: number,
    shapeMode: ShapeMode,
    uniformShape: CellObjectType,
    perRingShapes: CellObjectType[],
    perSectorShapes: CellObjectType[],
    rng: () => number,
): CellObjectType {
    switch (shapeMode) {
        case "uniform":
            return uniformShape;
        case "per-ring": {
            const arr = perRingShapes.length > 0 ? perRingShapes : [uniformShape];
            return arr[ring % arr.length];
        }
        case "per-sector": {
            const arr = perSectorShapes.length > 0 ? perSectorShapes : [uniformShape];
            return arr[sector % arr.length];
        }
        case "random": {
            const idx = Math.floor(rng() * CELL_OBJECT_TYPES.length);
            return CELL_OBJECT_TYPES[idx];
        }
    }
}

function resolveObjectSvgIndex(
    ring: number,
    sector: number,
    shapeMode: ShapeMode,
    uniformObjectSvgIndex: number,
    perRingObjectSvgIndices: number[],
    perSectorObjectSvgIndices: number[],
    objectSvgCount: number,
    rng: () => number,
): number {
    if (objectSvgCount <= 0) return -1;
    switch (shapeMode) {
        case "uniform":
            return uniformObjectSvgIndex;
        case "per-ring": {
            if (perRingObjectSvgIndices.length === 0) return uniformObjectSvgIndex;
            return perRingObjectSvgIndices[ring % perRingObjectSvgIndices.length] ?? -1;
        }
        case "per-sector": {
            if (perSectorObjectSvgIndices.length === 0) return uniformObjectSvgIndex;
            return perSectorObjectSvgIndices[sector % perSectorObjectSvgIndices.length] ?? -1;
        }
        case "random":
            return Math.floor(rng() * objectSvgCount);
    }
}

function shouldPopulateCell(
    ring: number,
    sector: number,
    rule: RingPopulationRule,
    rng: () => number,
): boolean {
    switch (rule.type) {
        case "all":
            return true;
        case "randomDensity": {
            const density = Math.max(0, Math.min(1, rule.density));
            const rolls = Math.max(0, Math.floor(rule.seedOffset ?? 0));
            for (let i = 0; i < rolls; i++) rng();
            return rng() < density;
        }
        case "everyN": {
            const step = Math.max(1, Math.floor(rule.step));
            const phase = ((Math.floor(rule.phase) % step) + step) % step;
            return (sector - phase) % step === 0;
        }
        case "fillNSkipN": {
            const fill = Math.max(1, Math.floor(rule.fill));
            const skip = Math.max(0, Math.floor(rule.skip));
            const period = fill + skip;
            const phase = ((Math.floor(rule.phase) % period) + period) % period;
            const idx = ((sector - phase) % period + period) % period;
            return idx < fill;
        }
        default:
            return ring >= 0;
    }
}

// ─── SVG shape builders (all centered at (cx, cy)) ────────────────────────────

function f(n: number): string {
    return String(+n.toFixed(2));
}

function buildShapeElement(
    shape: CellObjectType,
    cx: number,
    cy: number,
    size: number,
    rotDeg: number,
    fill: string,
    opacity: number,
): string {
    const r = size / 2;
    const fillAttr = `fill="${fill}"`;
    const opacityAttr = opacity < 1 ? ` opacity="${f(opacity)}"` : "";
    const tf =
        rotDeg !== 0
            ? ` transform="rotate(${f(rotDeg)},${f(cx)},${f(cy)})"`
            : "";

    switch (shape) {
        case "circle":
            return `<circle cx="${f(cx)}" cy="${f(cy)}" r="${f(r)}" ${fillAttr}${opacityAttr}${tf}/>`;

        case "square":
            return `<rect x="${f(cx - r)}" y="${f(cy - r)}" width="${f(size)}" height="${f(size)}" ${fillAttr}${opacityAttr}${tf}/>`;

        case "rounded-square": {
            const rx = f(r * 0.3);
            return `<rect x="${f(cx - r)}" y="${f(cy - r)}" width="${f(size)}" height="${f(size)}" rx="${rx}" ${fillAttr}${opacityAttr}${tf}/>`;
        }

        case "triangle": {
            const pts = [0, 1, 2]
                .map((i) => {
                    const a = -Math.PI / 2 + (i * 2 * Math.PI) / 3;
                    return `${f(cx + r * Math.cos(a))},${f(cy + r * Math.sin(a))}`;
                })
                .join(" ");
            return `<polygon points="${pts}" ${fillAttr}${opacityAttr}${tf}/>`;
        }

        case "diamond": {
            const pts = [
                `${f(cx)},${f(cy - r)}`,
                `${f(cx + r * 0.6)},${f(cy)}`,
                `${f(cx)},${f(cy + r)}`,
                `${f(cx - r * 0.6)},${f(cy)}`,
            ].join(" ");
            return `<polygon points="${pts}" ${fillAttr}${opacityAttr}${tf}/>`;
        }

        case "star-4": {
            const inner = r * 0.4;
            const pts = Array.from({ length: 8 }, (_, i) => {
                const a = -Math.PI / 2 + (i * Math.PI) / 4;
                const rad = i % 2 === 0 ? r : inner;
                return `${f(cx + rad * Math.cos(a))},${f(cy + rad * Math.sin(a))}`;
            }).join(" ");
            return `<polygon points="${pts}" ${fillAttr}${opacityAttr}${tf}/>`;
        }

        case "star-6": {
            const inner = r * 0.5;
            const pts = Array.from({ length: 12 }, (_, i) => {
                const a = -Math.PI / 2 + (i * Math.PI) / 6;
                const rad = i % 2 === 0 ? r : inner;
                return `${f(cx + rad * Math.cos(a))},${f(cy + rad * Math.sin(a))}`;
            }).join(" ");
            return `<polygon points="${pts}" ${fillAttr}${opacityAttr}${tf}/>`;
        }

        case "cross": {
            const arm = r;
            const w = r * 0.35;
            const pts = [
                `${f(cx - w)},${f(cy - arm)}`,
                `${f(cx + w)},${f(cy - arm)}`,
                `${f(cx + w)},${f(cy - w)}`,
                `${f(cx + arm)},${f(cy - w)}`,
                `${f(cx + arm)},${f(cy + w)}`,
                `${f(cx + w)},${f(cy + w)}`,
                `${f(cx + w)},${f(cy + arm)}`,
                `${f(cx - w)},${f(cy + arm)}`,
                `${f(cx - w)},${f(cy + w)}`,
                `${f(cx - arm)},${f(cy + w)}`,
                `${f(cx - arm)},${f(cy - w)}`,
                `${f(cx - w)},${f(cy - w)}`,
            ].join(" ");
            return `<polygon points="${pts}" ${fillAttr}${opacityAttr}${tf}/>`;
        }
    }
}

// ─── Custom SVG embedding ─────────────────────────────────────────────────────

interface ParsedSvg {
    viewBox: string;
    inner: string;
}

function parseSvgSymbol(svgMarkup: string): ParsedSvg | null {
    if (typeof DOMParser === "undefined") return null;
    try {
        const doc = new DOMParser().parseFromString(
            svgMarkup,
            "image/svg+xml",
        );
        const root = doc.documentElement;
        if (root.tagName.toLowerCase() === "parsererror") return null;
        if (!(root instanceof SVGSVGElement)) return null;
        const w = root.getAttribute("width") ?? "100";
        const h = root.getAttribute("height") ?? "100";
        const vb =
            root.getAttribute("viewBox") ?? `0 0 ${w} ${h}`;
        const inner = root.innerHTML;
        return { viewBox: vb, inner };
    } catch {
        return null;
    }
}

function buildCustomSvgElement(
    cx: number,
    cy: number,
    size: number,
    rotDeg: number,
    opacity: number,
    viewBox: string,
    inner: string,
): string {
    const opacityAttr = opacity < 1 ? ` opacity="${f(opacity)}"` : "";
    const tf =
        rotDeg !== 0
            ? ` transform="rotate(${f(rotDeg)},${f(cx)},${f(cy)})"`
            : "";
    const half = size / 2;
    return (
        `<g${tf}${opacityAttr}>` +
        `<svg x="${f(cx - half)}" y="${f(cy - half)}" width="${f(size)}" height="${f(size)}"` +
        ` viewBox="${viewBox}" preserveAspectRatio="xMidYMid meet" overflow="hidden">` +
        inner +
        `</svg></g>`
    );
}

// ─── Main builder ─────────────────────────────────────────────────────────────

export function buildPolarGridCellsSvg(config: PolarGridCellsConfig): string {
    const {
        rings,
        sectors,
        ringConfigs,
        innerRadiusFraction,
        cellPadding,
        shapeMode,
        uniformObjectSvgIndex,
        perRingObjectSvgIndices,
        perSectorObjectSvgIndices,
        uniformShape,
        perRingShapes,
        perSectorShapes,
        sizeMode,
        uniformSize,
        sizeMin,
        sizeMax,
        opacityMode,
        uniformOpacity,
        opacityMin,
        opacityMax,
        fill,
        rotateWithCell,
        useCustomSvg,
        svgSymbol,
        objectSvgs,
        seed,
        canvasSize,
    } = config;

    const clamp = (v: number, lo: number, hi: number) =>
        Math.max(lo, Math.min(hi, v));

    const rCount = Math.max(1, Math.round(rings));
    const resolvedRingConfigs = normalizeRingConfigs(rCount, ringConfigs, sectors);
    const margin = canvasSize * 0.04;
    const cx0 = canvasSize / 2;
    const cy0 = canvasSize / 2;
    const outerR = canvasSize / 2 - margin;
    const innerR = outerR * clamp(innerRadiusFraction, 0, 0.99);
    const pad = clamp(cellPadding, 0, 0.99);
    const weightSum = resolvedRingConfigs.reduce(
        (sum, rc) => sum + Math.max(0.01, rc.heightWeight),
        0,
    );
    const ringPitches = resolvedRingConfigs.map(
        (rc) => ((outerR - innerR) * Math.max(0.01, rc.heightWeight)) / weightSum,
    );

    let customSvgViewBox = "0 0 100 100";
    let customSvgInner = "";
    if (useCustomSvg && svgSymbol.trim()) {
        const parsed = parseSvgSymbol(svgSymbol);
        if (parsed) {
            customSvgViewBox = parsed.viewBox;
            customSvgInner = parsed.inner;
        }
    }
    const parsedObjectSvgs = objectSvgs
        .map((obj) => parseSvgSymbol(obj.content))
        .filter((p): p is ParsedSvg => !!p);

    const elements: string[] = [];

    let ringStart = innerR;
    for (let ring = 0; ring < rCount; ring++) {
        const rc = resolvedRingConfigs[ring];
        const ringPitch = ringPitches[ring];
        const ringEnd = ringStart + ringPitch;
        const rCenter = (ringStart + ringEnd) / 2;
        const sCount = Math.max(1, Math.round(rc.sectors));
        const arcLength = (2 * Math.PI * rCenter) / sCount;
        const cellSlotHeight = ringPitch * (1 - pad);
        const radialSize = cellSlotHeight * rc.cellHeightFactor;
        const tangentialSize = arcLength * (1 - pad);
        const maxCellSize = Math.min(radialSize, tangentialSize);
        const tRing = rCount > 1 ? ring / (rCount - 1) : 0;
        const ringRotationRad = (rc.rotationDeg * Math.PI) / 180;

        for (let sector = 0; sector < sCount; sector++) {
            const rng = cellRng(seed, ring, sector);
            const theta =
                -Math.PI / 2 + ((sector + 0.5) * 2 * Math.PI) / sCount + ringRotationRad;
            const px = cx0 + rCenter * Math.cos(theta);
            const py = cy0 + rCenter * Math.sin(theta);
            const rotDeg = rotateWithCell
                ? (theta * 180) / Math.PI + 90
                : 0;

            const objectIdxFromMode = resolveObjectSvgIndex(
                ring,
                sector,
                shapeMode,
                uniformObjectSvgIndex,
                perRingObjectSvgIndices,
                perSectorObjectSvgIndices,
                parsedObjectSvgs.length,
                rng,
            );
            const shape = resolveShape(
                ring,
                sector,
                shapeMode,
                uniformShape,
                perRingShapes,
                perSectorShapes,
                rng,
            );

            const sizeFraction = resolveScalar(
                sizeMode,
                uniformSize,
                sizeMin,
                sizeMax,
                tRing,
                rng,
            );
            const cellSize = sizeFraction * maxCellSize;

            const opacity = resolveScalar(
                opacityMode,
                uniformOpacity,
                opacityMin,
                opacityMax,
                tRing,
                rng,
            );

            if (!shouldPopulateCell(ring, sector, rc.populationRule, rng)) continue;
            if (cellSize <= 0.5) continue;

            let elem: string;
            const ringObjectIdx = rc.objectSvgIndex ?? -1;
            const finalObjectIdx =
                ringObjectIdx >= 0 ? ringObjectIdx : objectIdxFromMode;
            const resolvedObject =
                finalObjectIdx >= 0 && finalObjectIdx < parsedObjectSvgs.length
                    ? parsedObjectSvgs[finalObjectIdx]
                    : null;
            if (resolvedObject) {
                elem = buildCustomSvgElement(
                    px,
                    py,
                    cellSize,
                    rotDeg,
                    opacity,
                    resolvedObject.viewBox,
                    resolvedObject.inner,
                );
            } else if (useCustomSvg && customSvgInner) {
                elem = buildCustomSvgElement(
                    px,
                    py,
                    cellSize,
                    rotDeg,
                    opacity,
                    customSvgViewBox,
                    customSvgInner,
                );
            } else {
                elem = buildShapeElement(
                    shape,
                    px,
                    py,
                    cellSize,
                    rotDeg,
                    fill,
                    opacity,
                );
            }
            elements.push(elem);
        }
        ringStart = ringEnd;
    }

    const w = canvasSize;
    const h = canvasSize;
    return [
        `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${h}" width="${w}" height="${h}">`,
        `  <rect width="${w}" height="${h}" fill="white"/>`,
        ...elements,
        `</svg>`,
    ].join("\n");
}
